import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';

interface HeroBanner {
  id: string;
  category: string;
  product_name: string;
  price: number;
  original_price: number;
  badge: string;
  image_url: string;
  sort_order: number;
  is_active: boolean;
}

const CATEGORIES = ['Track Pants', 'T-Shirts', 'Jeans', 'Hoodies', 'Shorts'];
const BADGES = ['BESTSELLER', 'TOP SELLING', 'TRENDING', 'NEW', 'HOT', 'POPULAR', 'SALE'];

interface Props {
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}

const emptyForm = {
  category: 'Track Pants',
  product_name: '',
  price: '',
  original_price: '',
  badge: 'NEW',
  image_url: '',
  sort_order: 0,
  is_active: true,
};

export default function HeroBannerPanel({ onToast }: Props) {
  const [banners, setBanners] = useState<HeroBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [previewImg, setPreviewImg] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const fetchBanners = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('hero_banners')
      .select('*')
      .order('sort_order', { ascending: true });
    if (!error && data) setBanners(data as HeroBanner[]);
    setLoading(false);
  };

  useEffect(() => { fetchBanners(); }, []);

  const handleImageUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) { onToast('Sirf image file upload karo', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { onToast('File 5MB se zyada nahi honi chahiye', 'error'); return; }
    setUploadingImg(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `hero_${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('product-images')
        .upload(fileName, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('product-images').getPublicUrl(fileName);
      const url = urlData.publicUrl;
      setForm(f => ({ ...f, image_url: url }));
      setPreviewImg(url);
      onToast('Image upload ho gayi!', 'success');
    } catch {
      onToast('Image upload failed. URL manually paste karo.', 'error');
    } finally {
      setUploadingImg(false);
    }
  };

  const handleSave = async () => {
    if (!form.product_name.trim()) { onToast('Product name daalo', 'error'); return; }
    if (!form.image_url.trim()) { onToast('Image URL ya upload karo', 'error'); return; }
    if (!form.price) { onToast('Price daalo', 'error'); return; }
    setSaving(true);
    const payload = {
      category: form.category,
      product_name: form.product_name.trim(),
      price: Number(form.price),
      original_price: Number(form.original_price) || Number(form.price),
      badge: form.badge,
      image_url: form.image_url.trim(),
      sort_order: Number(form.sort_order) || 0,
      is_active: form.is_active,
      updated_at: new Date().toISOString(),
    };
    if (editingId) {
      const { error } = await supabase.from('hero_banners').update(payload).eq('id', editingId);
      if (error) { onToast('Update failed: ' + error.message, 'error'); }
      else { onToast('Banner update ho gaya!', 'success'); }
    } else {
      const { error } = await supabase.from('hero_banners').insert(payload);
      if (error) { onToast('Add failed: ' + error.message, 'error'); }
      else { onToast('Naya banner add ho gaya!', 'success'); }
    }
    setSaving(false);
    setEditingId(null);
    setShowAddForm(false);
    setForm(emptyForm);
    setPreviewImg('');
    fetchBanners();
  };

  const handleEdit = (b: HeroBanner) => {
    setEditingId(b.id);
    setForm({
      category: b.category,
      product_name: b.product_name,
      price: String(b.price),
      original_price: String(b.original_price),
      badge: b.badge,
      image_url: b.image_url,
      sort_order: b.sort_order,
      is_active: b.is_active,
    });
    setPreviewImg(b.image_url);
    setShowAddForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Is banner ko delete karna chahte ho?')) return;
    const { error } = await supabase.from('hero_banners').delete().eq('id', id);
    if (error) { onToast('Delete failed', 'error'); }
    else { onToast('Banner delete ho gaya!', 'success'); fetchBanners(); }
  };

  const handleToggleActive = async (b: HeroBanner) => {
    const { error } = await supabase.from('hero_banners').update({ is_active: !b.is_active }).eq('id', b.id);
    if (!error) {
      onToast(b.is_active ? 'Banner hide kar diya' : 'Banner show kar diya', 'info');
      fetchBanners();
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setShowAddForm(false);
    setForm(emptyForm);
    setPreviewImg('');
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <i className="ri-image-edit-line text-orange-600 text-lg"></i>
            </div>
            <div>
              <p className="text-gray-900 font-bold text-base">Home Page Banner Products</p>
              <p className="text-gray-400 text-xs">Hero section mein dikhne wale products manage karo</p>
            </div>
          </div>
          <button
            onClick={() => { setShowAddForm(true); setEditingId(null); setForm(emptyForm); setPreviewImg(''); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-bold hover:bg-orange-600 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Naya Banner Add Karo
          </button>
        </div>
      </div>

      {/* Add / Edit Form */}
      {showAddForm && (
        <div className="bg-white rounded-2xl border border-orange-200 p-5">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
              <i className={`${editingId ? 'ri-edit-line' : 'ri-add-circle-line'} text-orange-600`}></i>
            </div>
            <p className="text-gray-900 font-bold">{editingId ? 'Banner Edit Karo' : 'Naya Banner Add Karo'}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Category */}
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Category</label>
              <select
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-orange-400 outline-none"
              >
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>

            {/* Badge */}
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Badge</label>
              <select
                value={form.badge}
                onChange={e => setForm(f => ({ ...f, badge: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-orange-400 outline-none"
              >
                {BADGES.map(b => <option key={b}>{b}</option>)}
              </select>
            </div>

            {/* Product Name */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Product Name</label>
              <input
                type="text"
                value={form.product_name}
                onChange={e => setForm(f => ({ ...f, product_name: e.target.value }))}
                placeholder="e.g. Baggy Black Street"
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-orange-400 outline-none"
              />
            </div>

            {/* Price */}
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Price (₹)</label>
              <input
                type="number"
                value={form.price}
                onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                placeholder="699"
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-orange-400 outline-none"
              />
            </div>

            {/* Original Price */}
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Original Price (₹)</label>
              <input
                type="number"
                value={form.original_price}
                onChange={e => setForm(f => ({ ...f, original_price: e.target.value }))}
                placeholder="999"
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-orange-400 outline-none"
              />
            </div>

            {/* Sort Order */}
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Sort Order</label>
              <input
                type="number"
                value={form.sort_order}
                onChange={e => setForm(f => ({ ...f, sort_order: Number(e.target.value) }))}
                placeholder="1"
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-orange-400 outline-none"
              />
              <p className="text-gray-400 text-xs mt-1">Chhota number = pehle dikhega</p>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-3 pt-5">
              <button
                onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                className={`relative w-12 h-6 rounded-full transition-all cursor-pointer shrink-0 ${form.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${form.is_active ? 'translate-x-6' : 'translate-x-0'}`}></span>
              </button>
              <span className="text-sm font-semibold text-gray-700">{form.is_active ? 'Active (visible)' : 'Hidden'}</span>
            </div>

            {/* Image Upload / URL */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Product Image</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={form.image_url}
                  onChange={e => { setForm(f => ({ ...f, image_url: e.target.value })); setPreviewImg(e.target.value); }}
                  placeholder="Image URL paste karo ya neeche se upload karo"
                  className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-orange-400 outline-none"
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadingImg}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-bold hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {uploadingImg ? <><i className="ri-loader-4-line animate-spin"></i>Uploading...</> : <><i className="ri-upload-cloud-line"></i>Upload</>}
                </button>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleImageUpload(f); }}
              />
              {/* Preview */}
              {previewImg && (
                <div className="mt-2 relative w-24 h-32 rounded-xl overflow-hidden border border-gray-200">
                  <img src={previewImg} alt="preview" className="w-full h-full object-cover object-top" />
                  <button
                    onClick={() => { setPreviewImg(''); setForm(f => ({ ...f, image_url: '' })); }}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center cursor-pointer"
                  >
                    <i className="ri-close-line text-xs"></i>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-5">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-3 rounded-xl bg-orange-500 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-orange-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {saving ? <><i className="ri-loader-4-line animate-spin"></i>Saving...</> : <><i className="ri-save-line"></i>{editingId ? 'Update Banner' : 'Add Banner'}</>}
            </button>
            <button
              onClick={handleCancel}
              className="px-5 py-3 rounded-xl bg-gray-100 text-gray-700 font-bold text-sm hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center shrink-0">
            <i className="ri-lightbulb-flash-line text-amber-500 text-lg"></i>
          </div>
          <div>
            <p className="text-amber-800 font-bold text-sm mb-1">Yeh kaise kaam karta hai?</p>
            <ul className="text-amber-700 text-xs space-y-1">
              <li className="flex items-start gap-1"><i className="ri-check-line text-amber-500 shrink-0"></i>Home page ke hero section mein "Our Collection" mein yeh products dikhte hain</li>
              <li className="flex items-start gap-1"><i className="ri-check-line text-amber-500 shrink-0"></i>Category ke hisaab se products group hote hain (Track Pants, T-Shirts, etc.)</li>
              <li className="flex items-start gap-1"><i className="ri-check-line text-amber-500 shrink-0"></i>Image change karne ke liye "Edit" karo aur naya image upload karo</li>
              <li className="flex items-start gap-1"><i className="ri-check-line text-amber-500 shrink-0"></i>Sort order se control karo kaun sa product pehle dikhega</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Banners List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-orange-500 animate-spin"></div>
        </div>
      ) : banners.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto mb-3">
            <i className="ri-image-add-line text-3xl text-orange-400"></i>
          </div>
          <p className="text-gray-700 font-semibold">Koi banner nahi hai</p>
          <p className="text-gray-400 text-sm mt-1">Upar "Naya Banner Add Karo" button se add karo</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Category Groups */}
          {CATEGORIES.map(cat => {
            const catBanners = banners.filter(b => b.category === cat);
            if (catBanners.length === 0) return null;
            return (
              <div key={cat} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                  <i className="ri-folder-line text-gray-500 text-sm"></i>
                  <span className="text-gray-700 font-bold text-sm">{cat}</span>
                  <span className="ml-auto text-xs text-gray-400">{catBanners.length} products</span>
                </div>
                <div className="divide-y divide-gray-50">
                  {catBanners.map(b => (
                    <div key={b.id} className={`flex items-center gap-3 p-3 ${!b.is_active ? 'opacity-50' : ''}`}>
                      {/* Image */}
                      <div className="w-14 h-18 rounded-xl overflow-hidden border border-gray-100 shrink-0" style={{ height: '72px' }}>
                        <img src={b.image_url} alt={b.product_name} className="w-full h-full object-cover object-top" />
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-gray-900 font-bold text-sm truncate">{b.product_name}</span>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-black text-white bg-orange-500 whitespace-nowrap">{b.badge}</span>
                          {!b.is_active && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-gray-500 bg-gray-100">Hidden</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-gray-900 font-bold text-sm">₹{b.price}</span>
                          {b.original_price > b.price && (
                            <span className="text-gray-400 text-xs line-through">₹{b.original_price}</span>
                          )}
                          <span className="text-gray-400 text-xs">· Order #{b.sort_order}</span>
                        </div>
                      </div>
                      {/* Actions */}
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          onClick={() => handleToggleActive(b)}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all ${b.is_active ? 'bg-green-50 text-green-600 hover:bg-green-100' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                          title={b.is_active ? 'Hide karo' : 'Show karo'}
                        >
                          <i className={b.is_active ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
                        </button>
                        <button
                          onClick={() => handleEdit(b)}
                          className="w-8 h-8 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center cursor-pointer hover:bg-orange-100 transition-all"
                          title="Edit karo"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(b.id)}
                          className="w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-all"
                          title="Delete karo"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
